'use client';

import { VStack, HStack, Box, Divider } from '@terra/ui-styled-system';
import { Text } from '../../../../../components/styled/text';
import {
  Badge,
  BadgeSeverity,
  isBadgeSeverityType,
} from '../../../../../components/badge';
import { Card } from '../../../../../components/card';
import {
  AlertCircleIcon,
  Alert01Icon,
  ChartHistogramIcon,
  Bug01Icon,
  File01Icon,
  Globe02Icon,
  InformationCircleIcon,
  Shield01Icon,
  Target03Icon,
  Wrench01Icon,
} from '@terra/ui-icons';
import { MarkdownPreview } from '../../../../markdown-preview';
import { StructuredPoc } from '../../../../findings/StructuredPoc';
import { formatCategoryForDisplay } from '../../../../../utils/category-utils';
import { normalizeSeverityLabel } from '../../../../../utils/severity-label.utils';
import type { Finding } from './types';
import type { OnOpenChatObject } from '../../hitl-types';
import { useTranslations } from 'next-intl';

const PROOF_OF_CONCEPT_LABEL = 'Proof of Concept';
const NOTE_LABEL = 'Note';

type FindingCardProps = {
  finding: Finding;
  variant?: 'compact' | 'detailed';
  onOpenObject?: OnOpenChatObject;
};
const getSeverityIcon = (severity?: string) => {
  if (!severity) return <Bug01Icon size={12} />;
  const sev = severity.toLowerCase();

  if (sev === 'critical' || sev === 'high')
    return <AlertCircleIcon size={12} />;
  if (sev === 'medium') return <Alert01Icon size={12} />;
  if (sev === 'low' || sev === 'informative' || sev === 'info')
    return <InformationCircleIcon size={12} />;

  return <Bug01Icon size={12} />;
};

function FindingCardSeverityBadge({ severity }: { severity: string }) {
  const severityLabel = normalizeSeverityLabel(severity);
  const icon = getSeverityIcon(severity);

  if (isBadgeSeverityType(severityLabel)) {
    return (
      <BadgeSeverity
        size="sm"
        variant="outline"
        severity={severityLabel}
        icon={icon}
      >
        {severity}
      </BadgeSeverity>
    );
  }

  return (
    <Badge size="sm" variant="outline" icon={icon}>
      {severity}
    </Badge>
  );
}

function DetailedFieldSection({
  icon,
  label,
  value,
  isMarkdown = false,
}: {
  icon: React.ReactNode;
  label: string;
  value?: string;
  isMarkdown?: boolean;
}) {
  if (!value) return null;

  return (
    <VStack alignItems="start" gap={1} width="100%">
      <HStack gap={1} alignItems="center" color="fg.muted">
        {icon}
        <Text fontSize="xs" fontWeight="semibold">
          {label}
        </Text>
      </HStack>
      <Box
        width="100%"
        pl={4}
        borderLeftWidth="2px"
        borderLeftColor="border.subtle"
      >
        {isMarkdown ? (
          <Box p={2} borderRadius="sm" width="100%">
            <MarkdownPreview source={value} compact />
          </Box>
        ) : (
          <Text
            fontSize="xs"
            color="fg.default"
            whiteSpace="pre-wrap"
            lineHeight="1.5"
          >
            {value}
          </Text>
        )}
      </Box>
    </VStack>
  );
}

export function FindingCard({
  finding,
  variant = 'compact',
  onOpenObject,
}: FindingCardProps) {
  const tFinding = useTranslations('designSystem.findings.findingDrawer');
  const tTable = useTranslations('designSystem.findingsList.table');
  const tCard = useTranslations('designSystem.chat.findingCard');
  const isDetailed = variant === 'detailed';
  const displayTitle = finding.title || finding.findingTitle;
  const cvssScoreStr =
    typeof finding.cvssScore === 'number'
      ? finding.cvssScore.toFixed(1)
      : finding.cvssScore;
  const pocBlocks = finding.pocBlocks?.length ? finding.pocBlocks : null;
  const hasDetailedContent =
    finding.description ||
    finding.businessImpact ||
    finding.poc ||
    pocBlocks ||
    finding.mitigationPlan ||
    finding.exploitationGuide;
  const canOpenFinding = Boolean(finding.findingId && onOpenObject);
  const handleOpenFinding = () => {
    if (!finding.findingId || !onOpenObject) return;
    onOpenObject({
      type: 'finding',
      id: finding.findingId,
      label: displayTitle,
    });
  };

  return (
    <Card.Root
      flexShrink={0}
      cursor={canOpenFinding ? 'pointer' : 'default'}
      onClick={canOpenFinding ? handleOpenFinding : undefined}
      _hover={canOpenFinding ? { borderColor: 'border.emphasized' } : undefined}
    >
      <Card.Body p={isDetailed ? 3 : 2.5}>
        <VStack alignItems="start" gap={isDetailed ? 3 : 1.5} width="100%">
          {/* Header with badges */}
          <HStack
            alignItems="center"
            gap={1.5}
            flexWrap="wrap"
            width="100%"
            justifyContent="space-between"
          >
            <HStack gap={1.5} flexWrap="wrap">
              <Badge
                size="sm"
                variant={isDetailed ? 'fill' : 'emphasis'}
                leadingContent={<Bug01Icon size={10} />}
              >
                {tFinding('entityTitle')}
              </Badge>
              {finding.findingId && (
                <Badge
                  size="sm"
                  variant="outline"
                  icon={<Bug01Icon size={10} />}
                >
                  {tCard('findingIdShort', {
                    id: finding.findingId.slice(0, 8),
                  })}
                </Badge>
              )}
              {finding.severity ? (
                <FindingCardSeverityBadge severity={finding.severity} />
              ) : null}
              {finding.category && (
                <Badge size="sm" variant="outline">
                  {formatCategoryForDisplay(finding.category)}
                </Badge>
              )}
            </HStack>
            <HStack gap={1.5}>
              {(cvssScoreStr || finding.score) && (
                <Badge
                  size="sm"
                  variant="emphasis"
                  icon={<ChartHistogramIcon size={10} />}
                >
                  {tCard('cvssScore', {
                    label: tTable('cvss'),
                    score: cvssScoreStr ?? finding.score ?? '',
                  })}
                </Badge>
              )}
              {finding.endpointMatched !== undefined && (
                <Badge size="sm" variant="emphasis">
                  {finding.endpointMatched
                    ? tCard('endpointMatched')
                    : tCard('noMatch')}
                </Badge>
              )}
            </HStack>
          </HStack>

          {/* Title */}
          {displayTitle && (
            <Text
              fontSize={isDetailed ? 'sm' : 'xs'}
              fontWeight={isDetailed ? 'bold' : 'semibold'}
              color="fg.default"
            >
              {displayTitle}
            </Text>
          )}

          {/* Affected Endpoint */}
          {finding.affectedEndpoint && (
            <HStack gap={1.5} alignItems="center" color="fg.muted">
              <Globe02Icon size={12} />
              <Text fontSize="xs" fontFamily="mono">
                {finding.affectedEndpoint}
              </Text>
            </HStack>
          )}

          {/* CVSS Vector (detailed only) */}
          {isDetailed && finding.cvssVector && (
            <Text fontSize="xs" color="fg.subtle" fontFamily="mono">
              {finding.cvssVector}
            </Text>
          )}

          {/* Content preview for compact mode - only show if NOT showing detailed fields */}
          {!isDetailed && !hasDetailedContent && finding.content && (
            <Text
              fontSize="2xs"
              color="fg.muted"
              lineClamp={2}
              whiteSpace="pre-wrap"
            >
              {finding.content}
            </Text>
          )}

          {/* Detailed fields - show in detailed mode OR compact mode when we have detailed content */}
          {isDetailed && hasDetailedContent && (
            <>
              {isDetailed && <Divider />}

              <DetailedFieldSection
                icon={<File01Icon size={12} />}
                label="Description"
                value={finding.description}
                isMarkdown
              />

              <DetailedFieldSection
                icon={<Target03Icon size={12} />}
                label="Business Impact"
                value={finding.businessImpact}
                isMarkdown
              />

              {pocBlocks ? (
                <VStack alignItems="start" gap={1} width="100%">
                  <HStack gap={1} alignItems="center" color="fg.muted">
                    <Shield01Icon size={12} />
                    <Text fontSize="xs" fontWeight="semibold">
                      {PROOF_OF_CONCEPT_LABEL}
                    </Text>
                  </HStack>
                  <Box
                    width="100%"
                    pl={4}
                    borderLeftWidth="2px"
                    borderLeftColor="border.subtle"
                  >
                    <StructuredPoc blocks={pocBlocks} findingId={finding.id} />
                  </Box>
                </VStack>
              ) : (
                <DetailedFieldSection
                  icon={<Shield01Icon size={12} />}
                  label="Proof of Concept"
                  value={finding.poc}
                  isMarkdown
                />
              )}

              <DetailedFieldSection
                icon={<Wrench01Icon size={12} />}
                label="Mitigation Plan"
                value={finding.mitigationPlan}
                isMarkdown
              />

              <DetailedFieldSection
                icon={<Alert01Icon size={12} />}
                label="Exploitation Guide"
                value={finding.exploitationGuide}
                isMarkdown
              />

              {finding.findingNote && (
                <VStack alignItems="start" gap={1} width="100%">
                  <Text fontSize="xs" fontWeight="semibold" color="fg.muted">
                    {NOTE_LABEL}
                  </Text>
                  <Badge size="sm" variant="outline">
                    {finding.findingNote}
                  </Badge>
                </VStack>
              )}
            </>
          )}
        </VStack>
      </Card.Body>
    </Card.Root>
  );
}
